import { createClient } from '@supabase/supabase-js';
import logger from '../utils/logger';

// Get these values from your Supabase project settings
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl) {
  logger.error('REACT_APP_SUPABASE_URL environment variable is not set');
  throw new Error('Supabase URL is required. Please check your environment configuration.');
}

if (!supabaseAnonKey) {
  logger.error('REACT_APP_SUPABASE_ANON_KEY environment variable is not set');
  throw new Error(
    'Supabase anonymous key is required. Please check your environment configuration.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Authentication functions
export const signUp = async (email, password) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/home?type=signup`,
    },
  });
  return { data, error };
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  // Perform a purely local logout to avoid any remote 401/403 noise.
  // This clears browser-stored tokens and lets the app state drive UI.
  try {
    if (typeof window !== 'undefined') {
      const projectRef = (() => {
        try {
          const host = new URL(supabaseUrl).host;
          return host.split('.')[0];
        } catch (_) {
          return null;
        }
      })();
      if (projectRef) {
        const storageKey = `sb-${projectRef}-auth-token`;
        window.localStorage.removeItem(storageKey);
      }
      // Remove common legacy keys just in case
      window.localStorage.removeItem('supabase.auth.token');
      window.localStorage.removeItem('supabase.auth.token#');
    }
  } catch (e) {
    logger.warn('Local signOut cleanup error ignored:', e);
  }
  return { error: null };
};

export const getCurrentUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  return { user, error };
};

export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback);
};

export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  return { data, error };
};

export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  return { data, error };
};

// Resend email confirmation to the current user's email
export const resendEmailConfirmation = async (email) => {
  // Supabase v2 does not expose a direct resend endpoint; the recommended
  // approach is to call signUp on the same email to trigger a new email.
  // We pass a random password to avoid any confusion.
  try {
    const randomPassword = Math.random().toString(36).slice(2) + '!A9';
    const { error } = await supabase.auth.signUp({
      email,
      password: randomPassword,
      options: {
        emailRedirectTo: `${window.location.origin}/home?type=signup`,
      },
    });

    if (error) {
      // If user already exists, Supabase may return an error, but the email may still be sent
      // We surface the error to the caller to decide messaging
      return { error };
    }
    return { error: null };
  } catch (e) {
    return { error: e };
  }
};

// Delivery fee functions
export const getDeliveryFees = async () => {
  const { data, error } = await supabase
    .from('delivery_fee')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  
  return { data, error };
};

export const getDeliveryFeeById = async (id) => {
  const { data, error } = await supabase
    .from('delivery_fee')
    .select('*')
    .eq('id', id)
    .eq('is_active', true)
    .single();
  
  return { data, error };
};
